import { useEffect, useRef, type ReactNode } from 'react'
import Swiper from 'swiper'
import { A11y, Autoplay, Navigation, Pagination, Thumbs } from 'swiper/modules'

export interface BannerSlide {
  image: string
  /**
   * Describes the photograph. Now that the hero carries real images rather than sized
   * placeholders, they are content and not decoration, so they deserve a description
   * instead of the generic "Slide Background Image" they used to get.
   */
  alt?: string
  content: ReactNode
}

export interface BannerSliderProps {
  slides: BannerSlide[]
  thumbs: string[]
  prevArrow: string
  nextArrow: string
  /**
   * Give the content the whole row rather than seven columns of twelve.
   *
   * The seven is the template's, and it is right for a hero that is a headline and a
   * button: the five empty columns are where the photograph shows through, and copy set
   * across a full-width banner runs to a line length nobody reads.
   *
   * The resort's hero is two columns of its own — the invitation and the booking form —
   * and inside seven twelfths of the container the form came out 360px wide and sat in the
   * middle of the screen. `wide` is what lets it use the width the page has.
   *
   * Off by default, because three apps want the seven and one does not, and a default
   * that changed the hero of every app is the kind of change nothing fails on.
   */
  wide?: boolean
}

/**
 * The home page's hero.
 *
 * With one slide there is nothing to slide: it renders a plain banner — no swiper, no
 * thumb strip, no arrows — because a carousel of one is just furniture around a picture.
 * With two or more it is the slider it always was, driven by the thumbnail strip.
 *
 * This is its own component rather than two generic `<Slider>`s because the two
 * instances are **order-dependent** — the main slider is constructed with
 * `thumbs: { swiper: thumbSlider }`, so the thumb instance must already exist. In the
 * markup the thumb strip comes *after* the main slider, so child-effect ordering
 * would create them the wrong way round. Here one effect creates thumb-then-main
 * explicitly.
 *
 * The `pagination` option is retained even though `.ul-banner-2-slider-pagination` is
 * not currently in the markup: it is a harmless no-op, and it is what a pagination
 * element would hook into if one is added.
 */
export function BannerSlider({ slides, thumbs, prevArrow, nextArrow, wide = false }: BannerSliderProps) {
  const mainRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLDivElement>(null)

  const isStatic = slides.length < 2
  /** Seven columns of twelve, or all of them. See `wide`. */
  const column = wide ? 'col-12' : 'col-md-7'

  useEffect(() => {
    if (isStatic) return
    const mainEl = mainRef.current
    const thumbEl = thumbRef.current
    if (!mainEl || !thumbEl) return

    // The strip is 3-up by design, but Swiper silently disables loop mode when there
    // are fewer slides than it needs — and a looped, centred strip with too few slides
    // renders with gaps where the duplicates would be. So both follow the slide count.
    const perView = Math.min(3, thumbs.length)
    const canLoop = thumbs.length > perView

    // Order matters — see the note above.
    const thumbSlider = new Swiper(thumbEl, {
      modules: [A11y, Autoplay, Thumbs],
      slidesPerView: perView,
      spaceBetween: 10,
      slideToClickedSlide: true,
      loop: canLoop,
      autoplay: true,
      speed: 1000,
      centeredSlides: canLoop,
    })

    const mainSlider = new Swiper(mainEl, {
      modules: [A11y, Autoplay, Navigation, Pagination, Thumbs],
      slidesPerView: 1,
      // 1-up, so two slides are enough to loop.
      loop: slides.length > 1,
      autoplay: true,
      spaceBetween: 0,
      speed: 1000,
      navigation: {
        nextEl: '.ul-banner-2-slider-navigation .next',
        prevEl: '.ul-banner-2-slider-navigation .prev',
      },
      pagination: {
        el: '.ul-banner-2-slider-pagination',
        type: 'bullets',
        clickable: true,
      },
      thumbs: { swiper: thumbSlider },
    })

    // Pin both to logical slide 0 so the hero and the thumb strip agree on where they
    // start. Swiper 12 rotates a looped, centered slider's slides during init and does
    // not apply the same rotation to the 3-up thumb strip as to the 1-up hero, so
    // without this the two can begin one slide apart. Thumb→hero click mapping itself
    // is left to Swiper's Thumbs module, which handles real pointer events correctly.
    thumbSlider.slideToLoop(0, 0, false)
    mainSlider.slideToLoop(0, 0, false)

    return () => {
      mainSlider.destroy(true, true)
      thumbSlider.destroy(true, true)
    }
    // Rebuild if the slide count changes — the loop/perView config above depends on it.
  }, [isStatic, slides.length, thumbs.length])

  if (isStatic) {
    const only = slides[0]
    return (
      <section className="ul-banner ul-banner-2 ul-banner-2--static">
        <div className="ul-banner-2-slide">
          {only && <img src={only.image} alt={only.alt ?? ''} className="ul-banner-2-slide-bg-img" />}
          <div className="row gy-4 align-items-center">
            <div className={column}>
              <div className="ul-banner-txt">{only?.content}</div>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="ul-banner ul-banner-2">
      <div ref={mainRef} className="ul-banner-2-slider swiper">
        <div className="swiper-wrapper">
          {slides.map((slide, i) => (
            <div className="swiper-slide" key={i}>
              <div className="ul-banner-2-slide">
                {/* bg img */}
                <img src={slide.image} alt={slide.alt ?? ''} className="ul-banner-2-slide-bg-img" />
                <div className="row gy-4 align-items-center">
                  {/* banner text */}
                  <div className={column}>
                    <div className="ul-banner-txt">{slide.content}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* slider navigation */}
      <div className="ul-banner-2-slider-navigation">
        <button className="prev" aria-label="Previous slide">
          <img src={prevArrow} alt="arrow" />
        </button>
        <div ref={thumbRef} className="ul-banner-2-thumb-slider swiper">
          <div className="swiper-wrapper">
            {thumbs.map((src, i) => (
              <div className="swiper-slide" key={i}>
                <img src={src} alt="Banner Thumb" />
              </div>
            ))}
          </div>
        </div>
        <button className="next" aria-label="Next slide">
          <img src={nextArrow} alt="arrow" />
        </button>
      </div>
    </section>
  )
}
